/* eslint-disable @atlassian/tangerine/import/no-parent-imports */
/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag React.Fragment
 */
import React, { useMemo, useState } from 'react';

import { jsx } from '@compiled/react';

import { fg } from '@atlaskit/platform-feature-flags';
import { useAINudgesRefreshed, useAISurfaceMetadata } from '@atlassian/ai-smart-button-api';
import type { ConversationAction } from '@atlassian/conversation-assistant';
import { type RovoExperienceSidebarAPI } from '@atlassian/rovo-experience-api';

import { isBrowser } from '../../../common/utils/is-browser';
import { useFetchNudgeRecommendations } from '../../hooks/useFetchNudgeRecommendations';
import type { MigratedBaseConversationAction } from '../../types';
import { findActiveNudge } from '../../utils/findActiveNudge';

import { useNudgeSessionState } from './use-nudge-session-state';

// Lazy load to avoid loading useRovoActionTrigger when there's no active nudge
const LazyAIProactiveNudgeRefreshed = React.lazy(() =>
	import(/* webpackChunkName: "@atlaskit-internal_ai-proactive-nudge-refreshed" */ './main').then(
		(module) => ({
			default: module.AIProactiveNudgeRefreshed,
		}),
	),
);

type AIProactiveNudgeProps = {
	product?: string;
	sidebarApi: RovoExperienceSidebarAPI;
};

export const AIProactiveNudgeRefreshed = ({
	sidebarApi,
	product,
}: AIProactiveNudgeProps) => {
	const surfaceMetadata = useAISurfaceMetadata();
	const [triggerId] = useState(() => crypto.randomUUID());

	const selectedNudges = useAINudgesRefreshed();
	const shouldTrigger = Boolean(selectedNudges?.length);
	const pageUrl = isBrowser() ? `${window.location.origin}${window.location.pathname}` : '';
	const { recommendations, loading, error } = useFetchNudgeRecommendations(
		triggerId,
		shouldTrigger,
		pageUrl,
		surfaceMetadata?.product,
	);
	const { acceptedNudges, dismissedNudges, addAcceptedNudge, addDismissedNudge } =
		useNudgeSessionState();
	const potentialActiveNudgeLegacy: ConversationAction | null = useMemo(() => {
		if (fg('aix_nudge_recommendation_migration_enabled')) {
			return null;
		}
		/**
		 * ConversationAction must not be previously dismissed or accepted
		 * to be considered active.
		 */
		return (
			selectedNudges?.find(
				(nudge) => !dismissedNudges.includes(nudge.id) && !acceptedNudges.includes(nudge.id),
			) ?? null
		);
	}, [selectedNudges, dismissedNudges, acceptedNudges]);

	const potentialActiveNudge: ConversationAction | MigratedBaseConversationAction | null =
		useMemo(() => {
			if (potentialActiveNudgeLegacy) {
				return potentialActiveNudgeLegacy;
			}
			if (loading || error || !recommendations?.length || !selectedNudges?.length) {
				return null;
			}
			// TODO: dismiss/accept nudge by its nudgeContext.id so we only need to check recommendations
			// Remove conversation starter if dismissed or accepted
			const availableRecommendations = recommendations.filter(
				(recommendation) =>
					!dismissedNudges.includes(recommendation.feature_details.feature_id) &&
					!acceptedNudges.includes(recommendation.feature_details.feature_id),
			);
			// Remove actions if dismissed or accepted
			const availableNudges = selectedNudges.filter(
				(nudge) => !dismissedNudges.includes(nudge.id) && !acceptedNudges.includes(nudge.id),
			);
			return findActiveNudge(availableRecommendations, availableNudges) ?? null;
		}, [
			acceptedNudges,
			dismissedNudges,
			error,
			loading,
			potentialActiveNudgeLegacy,
			recommendations,
			selectedNudges,
		]);

	// Early exit if no nudge to show
	if (!potentialActiveNudge) {
		return null;
	}

	return (
		<React.Suspense fallback={null}>
			<LazyAIProactiveNudgeRefreshed
				sidebarApi={sidebarApi}
				product={product}
				potentialActiveNudge={potentialActiveNudge}
				addAcceptedNudge={addAcceptedNudge}
				addDismissedNudge={addDismissedNudge}
			/>
		</React.Suspense>
	);
};
